<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui';
import type { NativeDateInput } from '#layers/base/public';
import type { ResolvedMonospaceApiKey, ResolvedMonospaceServiceAccount, ResolvedMonospaceUser } from '#layers/data-model/public';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import UserAvatar from '#layers/access/components/User/UserAvatar.vue';
import { useAccessDisplayNames } from '#layers/access/composables/use-access-display-names';
import { useApiKeyDialogs } from '#layers/access/composables/use-api-key-dialogs';
import { useApiKeys } from '#layers/access/composables/use-api-keys';
import { useAuth } from '#layers/access/stores/auth';
import { formatExpiryLabel, formatInstantWithTimeZone, formatRelativeTime } from '#layers/base/public';
import { ListCard, OverflowMenu } from '#layers/ui/public';

interface ApiKeyListProps {
	apiKeys: ResolvedMonospaceApiKey[];
	allApiKeys?: ResolvedMonospaceApiKey[];
	totalItemsCount?: number;
	isLoading?: boolean;
	isService?: boolean;
	hideCreate?: boolean;
	serviceAccount?: ResolvedMonospaceServiceAccount;
}

const props = defineProps<ApiKeyListProps>();

const emit = defineEmits<{
	refresh: [];
	createServiceAccount: [];
}>();

const pageNumber = defineModel<number>('page', { default: 1 });
const pageSize = defineModel<number>('pageSize', { default: 25 });

const { deactivate, revoke, reactivate, isExpired } = useApiKeys();
const { createApiKey, editApiKey } = useApiKeyDialogs({
	onCreateServiceAccount: () => emit('createServiceAccount'),
});
const { currentUser } = useAuth();
const { getUserDisplayName } = useAccessDisplayNames();
const i18n = useI18n();
const { locale, t } = i18n;

function getIssuedAtBy(item: ResolvedMonospaceApiKey): string {
	if (item.creator?.id === currentUser.value?.id) return t('api_keys.issued_days_ago_by_you', { ago: formatRelativeTime(item.issuedAt, i18n) });
	return t('api_keys.issued_days_ago_by', { ago: formatRelativeTime(item.issuedAt, i18n), by: getUserDisplayName(item.creator as ResolvedMonospaceUser) });
}

function formatGeneralInstantTooltip(instant: NativeDateInput | null | undefined): string | undefined {
	if (!instant) return undefined;
	return formatInstantWithTimeZone(instant, { locales: locale.value });
}

const headerCount = computed(() => props.totalItemsCount ?? props.apiKeys.length);
const headerText = computed(() => t('api_keys.count_header', { n: headerCount.value }));
const currentItems = computed(() => props.allApiKeys?.length ?? props.apiKeys.length);

async function handleDeactivate(apiKey: ResolvedMonospaceApiKey) {
	await deactivate(apiKey);
	emit('refresh');
}

async function handleReactivate(apiKey: ResolvedMonospaceApiKey) {
	await reactivate(apiKey);
	emit('refresh');
}

async function handleRevoke(apiKey: ResolvedMonospaceApiKey) {
	await revoke(apiKey);
}

function getCreateTarget() {
	// When scoped to a service account, link the new key to it directly.
	if (props.serviceAccount) return props.serviceAccount;
	// In the general service-key list, let the dialog prompt for a service account.
	if (props.isService) return;
	return currentUser.value as ResolvedMonospaceUser;
}

async function handleCreate() {
	const created = await createApiKey(getCreateTarget());

	// ApiKeyDialog uses a custom POST endpoint that bypasses rstore mutations,
	// so the parent's queries don't auto-refresh — emit so they can.
	if (created) {
		emit('refresh');
	}
}

async function handleEdit(apiKey: ResolvedMonospaceApiKey) {
	const edited = await editApiKey(apiKey);

	if (edited) {
		emit('refresh');
	}
}

function getMenuItems(apiKey: ResolvedMonospaceApiKey): DropdownMenuItem[] {
	const items: DropdownMenuItem[] = [{
		label: t('with_ellipsis', { string: t('api_keys.edit') }),
		icon: 'lucide:pencil',
		onSelect: () => handleEdit(apiKey),
	}];

	if (apiKey.active) {
		items.push({
			label: t('api_keys.deactivate'),
			icon: 'lucide:x-circle',
			onSelect: () => handleDeactivate(apiKey),
		});
	}
	else {
		items.push({
			label: t('api_keys.reactivate'),
			icon: 'lucide:check-circle',
			onSelect: () => handleReactivate(apiKey),
		});
	}

	items.push({
		label: t('with_ellipsis', { string: t('api_keys.revoke') }),
		icon: 'lucide:trash-2',
		color: 'error',
		onSelect: () => handleRevoke(apiKey),
	});

	return items;
}
</script>

<template>
	<DSkeleton v-if="isLoading && headerCount === 0" class="h-14 w-full" />
	<ListCard
		v-else
		:items="apiKeys"
		selectable
		size="sm"
		:ui="{
			item: {
				base: 'cursor-pointer',
				label: 'flex items-center gap-2 font-medium',
				leading: 'flex size-8 items-center justify-center rounded-full bg-muted',
				trailing: 'flex items-center gap-2',
				description: 'flex flex-col gap-1 text-dimmed',
			},
		}"
		@select="(item) => handleEdit(item as ResolvedMonospaceApiKey)"
	>
		<template #header>
			{{ headerText }}
		</template>

		<template #header-actions>
			<DButton
				v-if="!hideCreate"
				color="neutral"
				variant="outline"
				size="sm"
				:label="$t('api_keys.create_title')"
				@click="handleCreate"
			/>
		</template>

		<template #item-leading="{ item }">
			<UserAvatar v-if="item.user" :user="item.user" class="size-8" :class="{ 'opacity-50': isExpired(item) }" />
		</template>

		<template #item-label="{ item }">
			<span :class="{ 'opacity-50': isExpired(item) }">{{ item.name }}</span>
			<template v-if="isService && item.user?.fullName">
				<span>&middot;</span>
				<span class="text-dimmed">{{ item.user.fullName }}</span>
			</template>
		</template>

		<template #item-description="{ item }">
			<template v-if="item.description">
				<div>
					<span class="text-muted">{{ item.description }}</span>
				</div>
			</template>
			<div class="flex items-start gap-1">
				<DTooltip :text="formatGeneralInstantTooltip(item.expiresAt)">
					<span>{{ formatExpiryLabel(item.expiresAt, i18n) }}</span>
				</DTooltip>
				<span>&middot;</span>
				<DTooltip :text="formatGeneralInstantTooltip(item.issuedAt)">
					<span>{{ getIssuedAtBy(item) }}</span>
				</DTooltip>
			</div>
		</template>

		<template #item-trailing="{ item }">
			<DBadge v-if="!item.active" color="error" variant="soft" size="md">
				{{ $t('api_keys.inactive') }}
			</DBadge>

			<OverflowMenu :items="getMenuItems(item as ResolvedMonospaceApiKey)" />
		</template>

		<template v-if="headerCount > pageSize" #footer>
			<DPaginationBar
				v-model:page="pageNumber"
				v-model:page-size="pageSize"
				:current-items
				:total-items="totalItemsCount"
				:loading="isLoading"
			/>
		</template>
	</ListCard>
</template>
